// Brief Prospection Phase 1 §4 — Page feedback post-événement (1-clic email).
// /feedback?token=…&rating=N — note ajustable, avis public optionnel, follow.
import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Star, Check, UserPlus, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FeedbackPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const urlStatus = params.get('status');
  const [info, setInfo] = useState(null);
  const [invalid, setInvalid] = useState(urlStatus === 'invalid' || !token);
  const [loading, setLoading] = useState(!!token);
  const [rating, setRating] = useState(Number(params.get('rating')) || 0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [reviewed, setReviewed] = useState(false);
  const [following, setFollowing] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/public/feedback/${token}/info`)
      .then((r) => {
        setInfo(r.data);
        if (r.data.rating) setRating(r.data.rating);
        setReviewed(!!r.data.already_reviewed);
        setFollowing(!!r.data.already_following);
      })
      .catch(() => setInvalid(true))
      .finally(() => setLoading(false));
  }, [token]);

  const rate = async (n) => {
    setRating(n);
    try {
      await axios.post(`${API}/public/feedback/${token}/rate`, { rating: n });
    } catch {
      toast.error('Impossible d\'enregistrer la note');
    }
  };

  const publishReview = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/public/feedback/${token}/review`, { comment, rating });
      setReviewed(true);
      toast.success('Merci ! Votre avis est publié.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Impossible de publier l\'avis');
    } finally {
      setBusy(false);
    }
  };

  const follow = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/public/feedback/${token}/follow`);
      setFollowing(true);
    } catch {
      toast.error('Impossible de suivre cet expert');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8]">
        <div className="animate-spin h-8 w-8 border-3 border-[#7F77DD] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (invalid || !info) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center p-6" data-testid="feedback-invalid">
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-10 max-w-md w-full text-center">
          <XCircle className="h-10 w-10 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-extrabold text-gray-900">Lien invalide ou expiré</h1>
          <p className="text-sm text-gray-500 mt-2">Ce lien de feedback n'est plus actif.</p>
          <Link to="/" className="mt-5 inline-block text-sm font-semibold text-[#7F77DD]">Découvrir OpenSapiens →</Link>
        </div>
      </div>
    );
  }

  const expert = info.expert || {};

  return (
    <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center p-6" data-testid="feedback-page">
      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8 sm:p-10 max-w-md w-full">
        <div className="text-center">
          {expert.avatar && (
            <img src={expert.avatar} alt={expert.display_name} className="h-16 w-16 rounded-full object-cover mx-auto mb-3 border-2 border-white shadow-md" />
          )}
          <h1 className="text-xl font-extrabold text-gray-900" data-testid="feedback-title">
            Merci d'avoir participé !
          </h1>
          <p className="text-sm text-gray-500 mt-1.5">
            Comment s'est passée <strong>{info.event_title}</strong> avec {expert.display_name} ?
          </p>
        </div>

        {/* Étoiles */}
        <div className="flex justify-center gap-2 mt-6" data-testid="feedback-stars">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              onClick={() => rate(n)}
              onMouseEnter={() => setHover(n)}
              onMouseLeave={() => setHover(0)}
              className="transition-transform hover:scale-110"
              data-testid={`feedback-star-${n}`}
              aria-label={`${n} étoiles`}
            >
              <Star className={`h-9 w-9 ${n <= (hover || rating) ? 'fill-amber-400 text-amber-400' : 'text-gray-200'}`} />
            </button>
          ))}
        </div>
        {rating > 0 && (
          <p className="text-center text-sm text-emerald-600 font-semibold mt-2" data-testid="feedback-rating-saved">
            Note enregistrée : {rating}/5 ✓
          </p>
        )}

        {/* Avis public */}
        {rating > 0 && !reviewed && (
          <div className="mt-6" data-testid="feedback-review-block">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
              Partagez votre expérience (avis public, optionnel)
            </p>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="Qu'avez-vous apprécié ?"
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#7F77DD]/40 resize-none"
              data-testid="feedback-comment"
            />
            <button
              onClick={publishReview}
              disabled={busy}
              className="mt-2 w-full py-3 rounded-2xl text-white font-bold shadow-md hover:opacity-90 transition-opacity disabled:opacity-60"
              style={{ backgroundColor: '#7F77DD' }}
              data-testid="feedback-publish-review"
            >
              Publier mon avis
            </button>
          </div>
        )}
        {reviewed && (
          <div className="mt-5 flex items-center justify-center gap-2 text-emerald-700 bg-emerald-50 rounded-xl py-2.5 text-sm font-semibold" data-testid="feedback-reviewed-badge">
            <Check className="h-4 w-4" /> Avis publié — merci !
          </div>
        )}

        {/* Follow */}
        <div className="mt-6 pt-5 border-t border-gray-100">
          {following ? (
            <p className="text-center text-sm text-gray-500" data-testid="feedback-following-badge">
              <Check className="h-4 w-4 inline text-emerald-600" /> Vous suivez {expert.display_name}
            </p>
          ) : (
            <button
              onClick={follow}
              disabled={busy}
              className="w-full py-3 rounded-2xl font-bold text-[#1D9E75] border-2 border-[#1D9E75]/40 hover:bg-[#1D9E75]/5 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="feedback-follow-btn"
            >
              <UserPlus className="h-4 w-4" /> Suivre {expert.display_name}
            </button>
          )}
          {expert.handle && (
            <Link
              to={`/@${expert.handle}`}
              className="mt-3 block text-center text-sm font-semibold text-[#7F77DD]"
              data-testid="feedback-profile-link"
            >
              Voir les prochaines sessions de {expert.display_name} →
            </Link>
          )}
        </div>

        <p className="mt-8 text-center text-xs text-gray-300">
          <Link to="/" className="hover:text-gray-500 transition-colors">Propulsé par <span className="font-semibold">OpenSapiens</span></Link>
        </p>
      </div>
    </div>
  );
}
